import { Input, Tooltip } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconLineClose } from "@kalodata/kds-design-icons";
import { IconHelp } from "@icons/index";
import { ClipUpload } from "@components/ClipUpload/ClipUpload";
import {
  CHARACTER_NAME_MAX_LENGTH,
  MAX_OTHER_CHARACTER_IMAGES,
  type CharacterFormState,
  type CharacterImageFile,
} from "./types";

const IMAGE_ACCEPT = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];

export interface CharacterEditorProps {
  form: CharacterFormState;
  imageFiles: CharacterImageFile[];
  isEditMode: boolean;
  onChangeForm: (patch: Partial<CharacterFormState>) => void;
  onFilesChange: (files: CharacterImageFile[]) => void;
  uploadFile: (file: File, ctx: any) => Promise<any>;
}

/**
 * Create / Edit Character 的主编辑表单。
 *
 * 设计稿节点：3536:24148（创建）/ 3536:24311（编辑）
 *
 * 结构：
 * 1. 顶部说明 banner（避免人脸/文字/配件/合影）
 * 2. 1 主图 + 3 其他视角图（共 4 槽位）
 * 3. Characters Name * 输入框（1–50 字）
 *
 * 底栏（Cancel / Save）由 index.tsx 通过 ClipModal 的 footerConfig 渲染。
 *
 * 编辑模式：当前后端 PUT 仅接受 name；图片槽位整体禁用并加 tooltip 提示。
 */
export const CharacterEditor = ({
  form,
  imageFiles,
  isEditMode,
  onChangeForm,
  onFilesChange,
  uploadFile,
}: CharacterEditorProps) => {
  const totalSlots = MAX_OTHER_CHARACTER_IMAGES + 1; // 1 main + 3 other

  const uploadNode = (
    <ClipUpload
      files={imageFiles}
      onFilesChange={onFilesChange}
      uploadFile={uploadFile}
      maxCount={totalSlots}
      accept={IMAGE_ACCEPT}
      compressImage
      cardWidth={158}
      cardHeight={158}
      gap={12}
      disabled={isEditMode}
      renderUploadedFile={(file, { remove, preview, width, height }) => {
        const isMain = imageFiles[0]?.uid === file.uid;
        // 若只剩一张图（即主图）且没有其他图，主图不允许删除（设计稿 3536:24423 注释）
        const canRemove = !isEditMode && (!isMain || imageFiles.length > 1);
        return (
          <div className="relative" style={{ width }}>
            <div
              className="group relative overflow-hidden rounded-[8px] border border-kds-border bg-kds-bg-card"
              style={{ width, height }}
            >
              {file.url && (
                <img
                  src={file.url}
                  alt={file.name}
                  className="h-full w-full cursor-pointer object-cover"
                  onClick={preview}
                />
              )}
              {canRemove && (
                <button
                  type="button"
                  aria-label={t("Remove")}
                  className="absolute top-[6px] right-[6px] hidden size-[20px] cursor-pointer items-center justify-center rounded-full border-none bg-kds-mask text-white group-hover:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove();
                  }}
                >
                  <IconLineClose size={12} />
                </button>
              )}
            </div>
          </div>
        );
      }}
    />
  );

  return (
    <div className="flex flex-col">
      {/* Banner */}
      <div className="-mx-[16px] -mt-[8px] mb-[16px] bg-kds-bg-subtle px-[16px] pt-[12px] pb-[10px]">
        <p className="m-0 text-[14px] leading-[1.4] text-kds-fg-soft">
          <Trans>
            Supports multi-angle photos of the main product. Please avoid images with faces, text, product accessories
            (e.g., replacement brush heads), or group shots (e.g., product sets).
          </Trans>
        </p>
      </div>

      <div className="mb-[16px]">{uploadNode}</div>

      {/* Characters Name */}
      <div className="mb-[16px] flex flex-col gap-[6px]">
        <div className="flex items-center gap-[4px] text-[16px] font-bold leading-[22px] text-kds-fg">
          <span>
            <Trans>Characters Name</Trans>
          </span>
          <span className="text-kds-danger-fg">*</span>
          <Tooltip title={t("Up to 50 characters. This name is shown in your library.")}>
            <IconHelp className="w-[14px] h-[14px] text-kds-fg-soft cursor-help" />
          </Tooltip>
        </div>
        <Input
          value={form.name}
          maxLength={CHARACTER_NAME_MAX_LENGTH}
          onChange={(e) => onChangeForm({ name: e.target.value })}
          placeholder={t("e.g., A white woman with curly hair")}
          className="h-[32px] text-[14px]"
        />
      </div>
    </div>
  );
};
